import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Label,
  Tooltip,
} from "recharts";

import {
  LegendItemProps,
  ChartDataPoint,
  LineChartCardProps,
} from "./utils/types";
import {
  LEGEND_LABELS,
  COLORS,
  CHART_CONFIG,
  AXIS_LABELS,
} from "./utils/constants";

const LegendItem: React.FC<LegendItemProps> = ({color, label}) => (
  <div className="d-flex align-items-center gap-2">
    <div
      className="rounded-circle"
      style={{backgroundColor: color, width: 10, height: 10}}
    />
    <span className="text-white" style={{fontSize: 10}}>
      {label}
    </span>
  </div>
);

const ChartHeader: React.FC<{
  title: string;
  icon: string;
  firstLineColor: string;
  secondLineColor: string;
}> = ({title, icon, firstLineColor, secondLineColor}) => (
  <div className="d-flex align-items-center gap-3">
    <div className="d-flex align-items-center gap-2">
      <div className="mb-2">
        <img src={icon} alt="Chart Icon" />
      </div>
      <h3 className="text-white font-semibold" style={{fontSize: 20}}>
        {title}
      </h3>
    </div>
    <div className="d-flex align-items-center space-x-6 gap-3">
      <LegendItem color={firstLineColor} label={LEGEND_LABELS.world} />
      <LegendItem color={secondLineColor} label={LEGEND_LABELS.russia} />
    </div>
  </div>
);

const CustomAxisLines: React.FC = () => (
  <>
    <div
      style={{
        position: "absolute",
        bottom: "29px",
        left: "0",
        right: "0",
        height: "1px",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: "10px",
          right: "-12px",
          height: "1px",
          backgroundColor: COLORS.axisLine,
        }}
      />
      {[
        {position: {left: "10px"}},
        {position: {left: "50%"}},
        {position: {right: "-12px"}},
      ].map((tick, index) => (
        <div
          key={`x-tick-${index}`}
          style={{
            position: "absolute",
            ...tick.position,
            width: "1px",
            top: "1px",
            height: "5px",
            backgroundColor: COLORS.axisLine,
          }}
        />
      ))}
    </div>
  </>
);

const Chart: React.FC<{
  data: ChartDataPoint[];
  firstLineColor: string;
  secondLineColor: string;
  borderColor: string;
  tooltipColor: string;
  category?: string;
}> = ({
  data,
  firstLineColor,
  secondLineColor,
  borderColor,
  tooltipColor,
  category,
}) => {
  const maxYValue = Math.max(...data.map((item) => item.вМире));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={CHART_CONFIG.margins}>
        <CartesianGrid
          strokeDasharray="0"
          stroke={COLORS.gridLine}
          vertical={true}
          horizontal={false}
        />
        <XAxis
          dataKey="год"
          axisLine={false}
          tickLine={false}
          tick={{
            fill: COLORS.axisLabel,
            fontSize: 8,
            textAnchor: "middle",
          }}
          domain={["dataMin", "dataMax"]}
          label={{
            value: AXIS_LABELS.x,
            position: "insideBottom",
            offset: 3,
            style: {
              textAnchor: "middle",
              fill: COLORS.axisLabel,
              fontSize: "16px",
            },
          }}
        />
        <YAxis
          axisLine={true}
          tickLine={false}
          tick={{
            fontSize: 6,
            offset: -20,
            textAnchor: "middle",
          }}
          domain={[0, maxYValue + maxYValue * 0.06]}
        >
          <Label
            value={
              category === "chart-investment" ? "Объем, млн $" : AXIS_LABELS.y
            }
            angle={-90}
            position="insideRight"
            dx={15}
            dy={0}
            style={{
              textAnchor: "middle",
              fill: COLORS.axisLabel,
              fontSize: "12px",
            }}
          />
        </YAxis>
        <Tooltip
          contentStyle={{
            backgroundColor: tooltipColor,
            border: `1px solid ${borderColor || COLORS.background}`,
            borderRadius: "8px",
            color: "#FFFFFF",
            fontSize: "12px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
          labelStyle={{
            color: "#FFFFFF",
            fontSize: "11px",
            marginBottom: "4px",
          }}
          itemStyle={{
            color: "#FFFFFF",
            fontSize: "12px",
          }}
          formatter={(value: number, name: string) => [
            `${value}`,
            name === "вМире" ? LEGEND_LABELS.world : LEGEND_LABELS.russia,
          ]}
          labelFormatter={(label: string) => `${AXIS_LABELS.x}: ${label}`}
        />
        <Line
          type="linear"
          dataKey="вМире"
          stroke={firstLineColor}
          strokeWidth={3}
          dot={false}
          activeDot={{r: 6, stroke: firstLineColor, strokeWidth: 2}}
        />
        <Line
          type="linear"
          dataKey="вРоссии"
          stroke={secondLineColor}
          strokeWidth={3}
          dot={false}
          activeDot={{r: 6, stroke: secondLineColor, strokeWidth: 2}}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

const LineChartCard: React.FC<LineChartCardProps> = ({
  data,
  title,
  icon,
  bgColor,
  firstLineColor,
  secondLineColor,
  borderColor,
  tooltipColor,
  category,
}) => {
  const containerStyle = {
    background: bgColor || COLORS.background,
    border: `1px solid ${borderColor}`,
    minHeight: "232px",
    width: "100%",
    borderRadius: CHART_CONFIG.borderRadius,
    padding: CHART_CONFIG.containerPadding,
  };

  const chartContainerStyle = {
    width: "100%",
    height: `${CHART_CONFIG.height}px`,
    position: "relative" as const,
  };

  return (
    <div className="gap-4" style={containerStyle}>
      <ChartHeader
        title={title}
        icon={icon}
        firstLineColor={firstLineColor}
        secondLineColor={secondLineColor}
      />

      <div style={chartContainerStyle} className="pt-2">
        <Chart
          data={data}
          firstLineColor={firstLineColor}
          secondLineColor={secondLineColor}
          borderColor={borderColor || COLORS.background}
          tooltipColor={tooltipColor || COLORS.background}
          category={category}
        />
        <CustomAxisLines />
      </div>
    </div>
  );
};

export default LineChartCard;
